/**
 * Topic filter interface for type-safe query keys
 * 
 * @property projectId - Filter by project ID
 * @property type - Filter by topic type
 * @property search - Filter by search term
 * @property sortBy - Field to sort by
 * @property sortDirection - Direction to sort (asc/desc)
 */
export interface TopicFilters {
  projectId?: string;
  type?: string | string[];
  search?: string;
  sortBy?: 'name' | 'lastUsed' | 'created';
  sortDirection?: 'asc' | 'desc';
}

/**
 * Query key factory for topics that enforces a normalized caching structure
 * 
 * These keys are used to identify and organize React Query cache entries
 * related to topics. Following TanStack Query best practices for structured keys.
 * 
 * # Hierarchical Data Structure
 * 
 * Our application has a hierarchical data structure:
 * - projects → topics → content (messages, notes, tasks, etc.)
 * 
 * This query key structure reflects and enforces this hierarchy to ensure:
 * 1. Efficient caching with separate topic lists per project
 * 2. Optimized project switching without losing cached data
 * 3. Proper updates when using realtime subscriptions
 * 
 * # Primary Query Key Patterns
 * 
 * ## Project-Topic Hierarchy
 * - `projectKeys.list()` - All projects
 * - `projectKeys.detail(projectId)` - A specific project
 * - `topicKeys.byProject(projectId)` - All topics in a specific project
 * - `topicKeys.detail(topicId)` - A specific topic's details
 * 
 * ## Importance of byProject Pattern
 * 
 * The `byProject` pattern is critical for our application. It allows us to:
 * - Maintain separate topic lists for each project in the cache
 * - Avoid clearing topics when switching projects
 * - Reduce network requests by reusing cached topic lists
 * - Ensure consistent data updates across components
 * 
 * # Selective Invalidation Structure
 * 
 * - topicKeys.all: Invalidates all topic-related queries
 * - topicKeys.lists(): Invalidates all topic list queries
 * - topicKeys.list(filters): Invalidates specific filtered lists
 * - topicKeys.byProject(projectId): Invalidates topics for a specific project
 * - topicKeys.details(): Invalidates all individual topic queries
 * - topicKeys.detail(id): Invalidates a specific topic
 * 
 * # Usage Examples
 * 
 * ```typescript
 * // Fetch all topics for a project (RECOMMENDED APPROACH)
 * useQuery({ 
 *   queryKey: topicKeys.byProject(projectId),
 *   queryFn: () => fetchTopicsForProject(projectId)
 * });
 * 
 * // Fetch a specific topic
 * useQuery({ 
 *   queryKey: topicKeys.detail(topicId), 
 *   queryFn: () => fetchTopic(topicId)
 * });
 * 
 * // Prefetch topics for a project when switching
 * queryClient.prefetchQuery({
 *   queryKey: topicKeys.byProject(targetProjectId),
 *   queryFn: () => fetchTopicsForProject(targetProjectId),
 *   staleTime: 30 * 1000
 * });
 * 
 * // Update topics in cache via realtime
 * queryClient.setQueriesData(
 *   { queryKey: topicKeys.byProject(projectId) },
 *   (oldData) => updatedData
 * );
 * ```
 * 
 * @see /documentation/REACT_QUERY_PROJECT_SWITCHING_OPTIMIZATION.md
 */
export const topicKeys = {
  /**
   * Base key for all topic-related queries
   * Invalidating this key will invalidate all topic queries
   */
  all: ['topics'] as const,
  
  /**
   * Keys for all topic lists
   * Invalidating this key will invalidate all list queries but not detail queries
   * 
   * @returns Tuple of ['topics', 'list']
   */
  lists: () => [...topicKeys.all, 'list'] as const,
  
  /**
   * Key for filtered topic lists
   * Used for storing and retrieving specific filtered views of topics
   * 
   * @param filters - Object containing filter criteria
   * @returns Tuple of ['topics', 'list', {...filters}]
   * 
   * @example
   * // Get topics for a specific project
   * topicKeys.list({ projectId: 'proj-123' })
   * 
   * @example
   * // Get topics of specific types
   * topicKeys.list({ projectId: 'proj-123', type: ['notes', 'ai_chat'] })
   * 
   * @example
   * // Get sorted topics
   * topicKeys.list({ projectId: 'proj-123', sortBy: 'lastUsed', sortDirection: 'desc' })
   */
  list: (filters: TopicFilters = {}) => [...topicKeys.lists(), filters] as const,
  
  /**
   * Keys for all individual topic details
   * Invalidating this will invalidate all individual topic queries
   * 
   * @returns Tuple of ['topics', 'detail']
   */
  details: () => [...topicKeys.all, 'detail'] as const,
  
  /**
   * Key for a specific topic by ID
   * Used for storing and retrieving data about a specific topic
   * 
   * @param id - Topic ID
   * @returns Tuple of ['topics', 'detail', id]
   * 
   * @example
   * // Get topic with ID "topic-123"
   * topicKeys.detail('topic-123')
   * 
   * @example
   * // Invalidate a specific topic
   * queryClient.invalidateQueries({ queryKey: topicKeys.detail('topic-123') })
   */
  detail: (id: string) => [...topicKeys.details(), id] as const,
  
  /**
   * Key for all topics in a specific project
   * 
   * THIS IS THE RECOMMENDED PRIMARY QUERY KEY to use for topic lists.
   * It's crucial for the proper functioning of project switching and 
   * maintaining separate topic lists per project in the cache.
   * 
   * Key features:
   * - Creates a stable, consistent query key for all topics in a project
   * - Enables efficient project switching without clearing topic data
   * - Allows for per-project topic list caching
   * - Works with realtime updates to maintain consistency
   * 
   * @param projectId - Project ID
   * @returns Tuple of ['topics', 'by-project', projectId]
   * 
   * @example
   * // Fetch topics for a project
   * useQuery({
   *   queryKey: topicKeys.byProject('proj-123'),
   *   queryFn: () => fetchTopics('proj-123')
   * });
   * 
   * @example
   * // Prefetch topics when switching projects
   * queryClient.prefetchQuery({
   *   queryKey: topicKeys.byProject('proj-123'),
   *   queryFn: () => fetchTopics('proj-123')
   * });
   * 
   * @example
   * // Update topics in a specific project (realtime)
   * queryClient.setQueriesData(
   *   { queryKey: topicKeys.byProject('proj-123') },
   *   (oldTopics) => newTopics
   * );
   */
  byProject: (projectId: string) => [...topicKeys.all, 'by-project', projectId] as const,
  
  /**
   * Key for messages in a specific topic
   * Used for topic-specific chat messages or content
   * 
   * @param topicId - Topic ID
   * @returns Tuple of ['topics', 'messages', topicId]
   */
  messages: (topicId: string) => [...topicKeys.all, 'messages', topicId] as const,
};